import { useState, useEffect, useCallback, useRef } from 'react';
import { logError } from './lib/logger';
import { Toaster } from './components/ui/sonner';
import { Button } from './components/ui/button';
import { claraChatSend, getDashboardStats, getAlerts, getAuthMembers, login } from './lib/api';
import { getUser, setSession, clearSession } from './lib/session';
import { Sparkle, Warning, Lightning, Files, Siren, ArrowRight, Copy, Check, Phone, Star, SignOut } from '@phosphor-icons/react';
import { toast } from 'sonner';

function WidgetLogin({ onLogin }) {
  const [members, setMembers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [passcode, setPasscode] = useState('');
  const [error, setError] = useState('');

  useEffect(() => { getAuthMembers().then(setMembers).catch(() => {}); }, []);

  useEffect(() => {
    if (!selected) return;
    const handleKey = (e) => {
      if (e.key >= '0' && e.key <= '9' && passcode.length < 4) setPasscode(p => p + e.key);
      if (e.key === 'Backspace') setPasscode(p => p.slice(0, -1));
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [selected, passcode.length]);

  useEffect(() => {
    if (passcode.length === 4 && selected) {
      login(selected.id, passcode).then(r => { setSession(r.token, r.user); onLogin(r.user); })
        .catch(() => { setError('Wrong passcode'); setPasscode(''); });
    }
  }, [passcode, selected, onLogin]);

  if (!selected) {
    return (
      <div className="p-3">
        <p className="text-xs font-semibold text-center mb-2">Sign in</p>
        <div className="space-y-1 max-h-[280px] overflow-y-auto">
          {members.map(m => (
            <button key={m.id} onClick={() => setSelected(m)}
              className="w-full flex items-center gap-2 p-2 rounded-lg border border-border bg-card hover:bg-muted text-left text-sm">
              <span className="w-7 h-7 rounded bg-[#E63935] text-white text-[10px] font-bold flex items-center justify-center">{m.initials}</span>
              <span className="font-medium text-foreground text-xs">{m.name}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 text-center">
      <p className="text-xs text-muted-foreground mb-1">{selected.name}</p>
      <div className="flex justify-center gap-2 mb-2">
        {[0,1,2,3].map(i => <div key={i} className={`w-3 h-3 rounded-full border-2 ${i < passcode.length ? 'bg-[#E63935] border-[#E63935]' : 'border-border'}`} />)}
      </div>
      {error && <p className="text-red-500 text-[10px] mb-1">{error}</p>}
      <p className="text-[10px] text-muted-foreground">Type your 4-digit passcode</p>
      <button onClick={() => { setSelected(null); setPasscode(''); }} className="text-[10px] text-muted-foreground hover:text-foreground mt-2">Back</button>
    </div>
  );
}

function MsgBubble({ msg }) {
  const [copied, setCopied] = useState(false);
  if (msg.role === 'user') return <div className="flex justify-end mb-2"><div className="max-w-[90%] bg-[#E63935] text-white rounded-xl rounded-br-sm px-3 py-2 text-xs">{msg.text}</div></div>;
  return (
    <div className="flex justify-start mb-2">
      <div className="max-w-[90%]">
        {msg.isFrustrated && <div className="bg-red-500/15 border border-red-500/40 rounded-lg px-2 py-1 mb-1 flex items-center gap-1"><Phone size={12} className="text-red-500" /><span className="text-red-500 text-[9px] font-bold uppercase">Phone Call First</span></div>}
        <div className="bg-card border border-border rounded-xl rounded-bl-sm px-3 py-2 text-xs text-foreground whitespace-pre-wrap">{msg.text}</div>
        <button onClick={() => { navigator.clipboard.writeText(msg.text); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
          className="text-[9px] text-muted-foreground hover:text-foreground mt-0.5 ml-1 flex items-center gap-0.5">
          {copied ? <><Check size={8} /> Copied</> : <><Copy size={8} /> Copy</>}
        </button>
      </div>
    </div>
  );
}

export default function WidgetApp() {
  const [user, setUser] = useState(() => getUser());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [tab, setTab] = useState('clara');
  const bottomRef = useRef(null);
  const sessionId = useRef(`w-${Date.now()}`);

  useEffect(() => { if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const loadStats = useCallback(async () => {
    try {
      const [s, a] = await Promise.all([getDashboardStats(), getAlerts()]);
      setStats(s); setAlerts(a);
    } catch (e) { logError('Widget stats load error:', e); }
  }, []);

  useEffect(() => { if (user) { loadStats(); const iv = setInterval(loadStats, 10000); return () => clearInterval(iv); } }, [user, loadStats]);

  const sendMessage = async (text) => {
    if (!text.trim() || loading) return;
    setMessages(p => [...p, { role: 'user', text: text.trim() }]);
    setInput('');
    setLoading(true);
    try {
      const res = await claraChatSend(text.trim(), `Widget, ${user?.name}`, sessionId.current, user?.id);
      setMessages(p => [...p, { role: 'clara', text: res.response, isFrustrated: res.is_frustrated_mode, hasWow: res.has_wow_suggestion }]);
    } catch (e) {
      setMessages(p => [...p, { role: 'clara', text: 'Sorry, try again.' }]);
    } finally { setLoading(false); }
  };

  if (!user) return <div className="min-h-screen bg-background"><Toaster position="top-center" /><div className="bg-[#0D0D0D] px-3 py-2 flex items-center gap-2"><div className="w-6 h-6 rounded bg-[#E63935] flex items-center justify-center"><Sparkle size={12} weight="fill" className="text-white" /></div><span className="text-white text-xs font-semibold">Daily Burn Widget</span></div><WidgetLogin onLogin={setUser} /></div>;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Toaster position="top-center" />
      {/* Header */}
      <div className="bg-[#0D0D0D] px-3 py-2 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded bg-[#E63935] flex items-center justify-center"><Sparkle size={12} weight="fill" className="text-white" /></div>
          <span className="text-white text-xs font-semibold">Daily Burn</span>
          <span className="text-white/40 text-[9px]">{user.name?.split(' ')[0]}</span>
        </div>
        <div className="flex items-center gap-1">
          <a href={window.location.origin} target="_blank" rel="noreferrer" className="text-white/50 hover:text-white text-[9px] px-1.5 py-0.5 rounded bg-white/10 hover:bg-white/20 transition-colors">Full App</a>
          <button onClick={() => { clearSession(); setUser(null); }} className="text-white/40 hover:text-red-400 p-1"><SignOut size={12} /></button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border bg-card flex-shrink-0">
        <button onClick={() => setTab('clara')} className={`flex-1 py-2 text-[10px] font-semibold transition-colors ${tab === 'clara' ? 'text-[#E63935] border-b-2 border-[#E63935]' : 'text-muted-foreground'}`}>Clara</button>
        <button onClick={() => setTab('stats')} className={`flex-1 py-2 text-[10px] font-semibold transition-colors ${tab === 'stats' ? 'text-[#E63935] border-b-2 border-[#E63935]' : 'text-muted-foreground'}`}>
          Stats {alerts.length > 0 && <span className="ml-1 bg-red-500 text-white text-[8px] px-1 rounded-full">{alerts.length}</span>}
        </button>
      </div>

      {/* Content */}
      {tab === 'clara' && (
        <div className="flex-1 flex flex-col min-h-0">
          <div className="flex-1 overflow-y-auto px-3 py-2">
            {messages.length === 0 && (
              <div className="text-center py-4">
                <Sparkle size={24} className="text-[#E63935] mx-auto mb-2" />
                <p className="text-xs font-semibold mb-1">Ask Clara anything</p>
                <p className="text-[10px] text-muted-foreground mb-3">Paste an email, ask for help, or draft a response</p>
                <div className="space-y-1">
                  {['Draft email response', 'Frustrated client help', 'WOW recovery idea', 'Realtor value pitch'].map(q => (
                    <button key={q} onClick={() => setInput(q + ': ')} className="w-full text-left px-2 py-1.5 rounded border border-border bg-card hover:bg-muted text-[10px]">{q}</button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => <MsgBubble key={`wm-${i}-${m.role}`} msg={m} />)}
            {loading && <div className="flex gap-1 ml-2 mb-2">{[0,1,2].map(i => <span key={i} className="w-1.5 h-1.5 bg-[#E63935] rounded-full animate-bounce" style={{ animationDelay: `${i * 150}ms` }} />)}</div>}
            <div ref={bottomRef} />
          </div>
          <div className="px-2 pb-2 pt-1 flex-shrink-0 border-t border-border flex gap-1.5">
            <textarea value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(input); } }}
              placeholder="Ask Clara..." className="flex-1 bg-card border border-border rounded-lg px-2 py-1.5 text-xs resize-none min-h-[32px] max-h-[80px] focus:outline-none focus:ring-1 focus:ring-[#E63935]/50" rows={1} />
            <Button onClick={() => sendMessage(input)} disabled={!input.trim() || loading} className="h-[32px] w-[32px] rounded-lg bg-[#E63935] hover:bg-[#d32f2f] p-0"><ArrowRight size={14} weight="bold" className="text-white" /></Button>
          </div>
        </div>
      )}

      {tab === 'stats' && (
        <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2">
          {stats && (
            <div className="grid grid-cols-2 gap-2">
              {[
                { label: 'Files', value: stats.total_files, icon: Files, color: 'text-blue-400' },
                { label: 'Urgent', value: stats.urgent_files, icon: Lightning, color: 'text-red-400' },
                { label: 'Escalations', value: stats.open_escalations, icon: Siren, color: 'text-red-400' },
                { label: 'Follow Ups', value: stats.needs_follow_up, icon: Warning, color: 'text-amber-400' },
              ].map(s => (
                <div key={s.label} className="bg-card border border-border rounded-lg p-2.5">
                  <div className="flex items-center gap-1.5 mb-0.5"><s.icon size={12} className={s.color} /><span className="text-[10px] text-muted-foreground">{s.label}</span></div>
                  <span className={`text-xl font-bold ${s.color}`}>{s.value}</span>
                </div>
              ))}
            </div>
          )}
          {alerts.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Active Alerts</p>
              <div className="space-y-1">
                {alerts.slice(0, 8).map(a => (
                  <div key={a.id || a.message} className={`rounded-lg border px-2.5 py-2 text-[10px] flex items-start gap-1.5 ${a.severity === 'critical' ? 'bg-red-500/10 border-red-500/30 text-red-400' : 'bg-amber-500/10 border-amber-500/30 text-amber-400'}`}>
                    <Warning size={11} weight="fill" className="mt-0.5 flex-shrink-0" /><span className="leading-snug">{a.message}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {!stats && <p className="text-xs text-muted-foreground text-center py-4">Loading...</p>}
        </div>
      )}
    </div>
  );
}
